import { isScheduleSharePath } from "./schedules/scheduleConstants";

/**
 * The project the sidebar's project rail shows (#3544), or null outside a
 * project: its overview page, a ?project=-scoped list, or a schedule's editor.
 * HomeSideBar swaps in ProjectSidebarNav for it; the workspace switcher closes
 * it on a switch (switchClosesProject).
 */
export const projectRailId = (
  pathname: string,
  search: string,
  activeScheduleProjectId: string | null,
): string | null => {
  const projectOverviewMatch = pathname.match(/^\/home\/projects\/([^/]+)\/?$/);
  // "new" is the static /home/projects/new route (NewProjectPage), not a
  // project id — same guard as editorConceptId in HomeLayout.tsx.
  const overviewProjectId =
    projectOverviewMatch?.[1] && projectOverviewMatch[1] !== "new"
      ? projectOverviewMatch[1]
      : null;
  const projectSearchParam = new URLSearchParams(search).get("project");
  const isProjectScopedList =
    (pathname === "/home/boards" ||
      pathname === "/home/collections" ||
      pathname === "/home/concepts" ||
      pathname === "/home/schedules" ||
      // The library is NOT filtered by ?project= — it searches the whole
      // catalog, which is the point of Discover. The param is navigational
      // only: it keeps the project rail and the way back when you enter the
      // library from a project overview, instead of dumping the user on a
      // global surface with no thread home.
      pathname === "/home/library") &&
    !!projectSearchParam;
  // A schedule always belongs to a project, so its editor keeps the project
  // rail too (#3621). The id isn't in the URL — ScheduleEditorPage publishes it
  // once the doc loads, so the rail swaps in a beat after a cold open. Its
  // public share view publishes it too but has no sidebar, so it isn't one.
  const scheduleProjectId =
    pathname.startsWith("/home/schedules/") && !isScheduleSharePath(pathname)
      ? activeScheduleProjectId
      : null;
  return (
    overviewProjectId ??
    (isProjectScopedList ? projectSearchParam : null) ??
    scheduleProjectId
  );
};

/**
 * Whether a workspace switch closes the open project: wherever the rail shows,
 * and in a schedule's editor before its doc has published the project.
 */
export const switchClosesProject = (
  pathname: string,
  search: string,
  activeScheduleProjectId: string | null,
): boolean =>
  !!projectRailId(pathname, search, activeScheduleProjectId) ||
  (/^\/home\/schedules\/[^/]+/.test(pathname) &&
    !isScheduleSharePath(pathname));

/**
 * A create page opened from a project (?project=, no rail) keeps its draft on a
 * switch and only drops the old project; the new item then lands unfiled in the
 * new workspace, as the backend's same-workspace filing rule would leave it.
 * Null when there's nothing to drop. The board editor files into its project's
 * team on purpose (createFirmFor), so it's left alone.
 */
export const withoutProjectParam = (
  pathname: string,
  search: string,
): string | null => {
  const params = new URLSearchParams(search);
  if (!pathname.startsWith("/home/") || !params.has("project")) return null;
  params.delete("project");
  const rest = params.toString();
  return rest ? `${pathname}?${rest}` : pathname;
};
